import axios, { AxiosResponse } from "axios"
import { CartContents } from "../contexts/CartContext";
import AbstractCommonApi from './AbstractCommonClient';

class Shipping extends AbstractCommonApi {

  // In-flight coalescing for getShipping. Multiple consumers (Shipping,
  // Taxes, Total via CartUtils, DeliveryDescription) all fetch the same
  // payload on every cart change and each starts its own POST. Keying
  // by (url, method, address, value, cart-items) collapses the duplicates
  // into one network round-trip; entries are removed on settle so a
  // genuine later refetch still goes through.
  private getShippingInflight = new Map<string, Promise<ShippingDetails>>();

  // Build a tenant-scoped URL. When `store` is provided we route to that
  // tenant explicitly, otherwise we use the active tenant (existing behavior).
  private url = (path: string, store?: string): string => {
    return store
      ? `${this.baseUrl}/${store}${path}`
      : `${this.extendedUrl}${path}`;
  };

  // Network-error-safe rethrow. `error.response` is missing on CORS / network
  // failures; the old `throw new Error(error)` produced empty messages and
  // unhandled rejections.
  private wrap = (error: any): Error => {
    const msg = error?.response?.data ?? error?.message ?? String(error);
    return new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
  };

  getRawShippingEstimate = async (province: string, postal: string, value: number, store?: string): Promise<ShippingDetails[]> => {

    var formData: Record<string, any> = {};
    formData['province'] = province;
    formData['postal'] = postal;
    formData['value'] = value;

    try {
        const results = await axios.post(this.url(`/api/v1/shipping/estimate`, store), formData);
        return results.data;
    } catch (error: any) {
      console.error(error);
      throw this.wrap(error);
    }
  }

  getShippingEstimate = async (cart: CartContents, store?: string): Promise<ShippingDetails[]> => {

    const deliveryAddress = cart.deliveryAddress;

    var formData: Record<string, any> = {};
    formData['postal'] = deliveryAddress.postal;
    formData['cart'] = cart;

    try {
        const results = await axios.post(this.url(`/api/v1/shipping/estimate`, store), formData);
        return results.data;
    } catch (error: any) {
      console.error(error);
      throw this.wrap(error);
    }
  }

  doGetBestShippingEstimate = async(cart: CartContents, store?: string): Promise<ShippingDetails|undefined> => {
        if (cart.deliveryAddress.postal) {
          const shippingDetailsList = await this.getShippingEstimate(cart, store);

          if (shippingDetailsList) {
            let useShipping: ShippingDetails | undefined = undefined;
            for (let i = 0; i < shippingDetailsList.length; i++) {
              const ship = shippingDetailsList[i];
              switch (ship.code) {
                case "PICKUP":
                case "LOCAL":
                  break;
                default:
                  if (!useShipping) {
                    if (ship.freeShippingThreshold) {
                      useShipping = ship;
                    }
                  }
                  else {
                    if (ship.freeShippingThreshold && useShipping.freeShippingThreshold &&
                        ship.freeShippingThreshold < useShipping.freeShippingThreshold) {
                      useShipping = ship;
                    }
                  }
                  break;
              }
            }
            return useShipping;
          }
        }
        return undefined;
    }

    getShipping = async (
        method: string,
        cart: CartContents,
        value: number,
        alwaysVisible?: boolean,
        store?: string): Promise<ShippingDetails> => {

        const deliveryAddress = cart.deliveryAddress;

        var formData: Record<string, any> = {};
        formData['street'] = deliveryAddress.street;
        formData['city'] = deliveryAddress.city;
        formData['province'] = deliveryAddress.province;
        formData['postal'] = deliveryAddress.postal;
        formData['value'] = value;
        formData['cart'] = cart;

        const url = this.url(`/api/v1/shipping`, store);

        // Cache key — items by id+count is enough for the backend's
        // mixed-tenant shipping logic without dumping the whole cart shape.
        const itemsKey = Object.values(cart.items)
            .map((ci: any) => `${ci.item?.id}:${ci.count}:${ci.tenant ?? ''}`)
            .sort()
            .join('|');
        const cacheKey = JSON.stringify({
            url,
            method,
            value,
            alwaysVisible: !!alwaysVisible,
            street: deliveryAddress.street ?? '',
            city: deliveryAddress.city ?? '',
            province: deliveryAddress.province ?? '',
            postal: deliveryAddress.postal ?? '',
            items: itemsKey,
        });

        const existing = this.getShippingInflight.get(cacheKey);
        if (existing) {
            return existing;
        }

        const promise = (async () => {
        try {
            const result = await axios.post(url, formData);

            const shippingOptions: ShippingDetails[] = result.data;

            // Synthetic "STANDARD" radio code: pick the first non-PICKUP option
            // from whatever the backend returned. Avoids hardcoding a specific
            // carrier code (CP.RP / ARMOUR / etc.) that may or may not be valid
            // for the current address.
            if (method === 'STANDARD') {
                for (const shipping of shippingOptions) {
                    if (shipping.code !== 'PICKUP') {
                        return shipping;
                    }
                }
                return { code: 'ERROR', name: 'Unknown', price: -1 };
            }

            for (let i = 0; i < shippingOptions.length; i++) {
                const shipping = shippingOptions[i];
                if (method === shipping.code) {
                    return shipping;
                }
            }
            if (alwaysVisible && method === "LOCAL") {
                return {
                    code: "ERROR",
                    name: "Local Delivery",
                    minDays: 0,
                    maxDays: 1,
                    price: -1
                };
            }
            return {code: 'ERROR', name: 'Unknown', price: -1}
        } catch (error: any) {
            throw this.wrap(error);
        }
        })();
        // Always clear the in-flight slot once settled so a later genuine
        // refetch (e.g. address changes) is allowed through.
        promise.finally(() => {
            this.getShippingInflight.delete(cacheKey);
        });
        this.getShippingInflight.set(cacheKey, promise);
        return promise;
    }

    getCities = async (province?: string, store?: string): Promise<string[]> => {
        if (!province) {
        return [];
        }
        try {
        const roles: AxiosResponse<string[]> = await axios.get(this.url(`/api/v1/shipping/cities/${province}`, store));
        if (!roles.data) {
            return [];
        }

        return roles.data;
        } catch (error: any) {
        throw this.wrap(error);
        }
    }
}

export default new Shipping();
